import { useState } from "react";
import { Link, useNavigate } from "react-router";
import { useTenant } from "../../../Contexts/TenantContext";
import { useAuth } from "../../../Contexts/AuthContext";
import { UserRegister } from "../../../Models/User";
import { CustomerType } from "../../../Enums/CustomerType";
import { toPublicPath } from "../../../Utils/publicRoutes";
import { formatCnpj, formatCpf, formatPhoneNumber, unformat } from "../../../Utils/DataFormatters";
import { isValidPassword, passwordRequirementsMessage } from "../../../Utils/passwordValidator";
import { Input } from "../../../Components/Input/Input";
import { FaUserPlus } from "react-icons/fa";
import { GoogleIcon } from "../../../Components/GoogleIcon/GoogleIcon";
import { toast } from "react-toastify";
import TrustBar from "../../../Components/TrustBar/TrustBar";
import Footer from "../../../Components/Footer/Footer";
import LoadingOverlay from "../../../Components/LoadingOverlay/LoadingOverlay";
import Button from "../../../Components/Button/Button";
import PersonTypeToggle from "../../../Components/PersonTypeToggle/PersonTypeToggle";

const UserSignUpPage = () => {
  const navigate = useNavigate();

  const { tenant } = useTenant();
  const { register, login, googlePopupLogin } = useAuth();

  const handleGoogleSignUp = () => {
    void googlePopupLogin('signup').then((authenticatedUser) => {
      if (authenticatedUser?.requiresContactInfoUpdate) {
        navigate(toPublicPath("/completar-cadastro"));
      } else if (authenticatedUser) {
        navigate("/");
      }
    });
  };

  const [personType, setPersonType] = useState(CustomerType.IndividualPerson);
  const [cpf, setCpf] = useState("");
  const [cnpj, setCnpj] = useState("");
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handlePersonTypeChange = (type: CustomerType) => {
    setPersonType(type);
    setCpf("");
    setCnpj("");
    setPhoneNumber("");
    setEmail("");
    setPassword("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (isSubmitting) {
      return;
    }

    if (!email || !phoneNumber) {
      toast.error("Preencha email e telefone.");
      return;
    }

    if (personType === CustomerType.IndividualPerson) {
      if (!cpf || cpf.length < 11) {
        toast.error("Preencha o CPF.");
        return;
      }
    } else {
      if (!cnpj || cnpj.length < 14) {
        toast.error("Preencha o CNPJ.");
        return;
      }
    }

    if (!isValidPassword(password)) {
      toast.error(passwordRequirementsMessage);
      return;
    }

    let payload: UserRegister;

    if (personType === CustomerType.IndividualPerson) {
      payload = {
        email,
        phoneNumber,
        password,
        customerType: CustomerType.IndividualPerson,
        individualPerson: { cpf },
        legalEntity: null,
      };
    } else {
      payload = {
        email,
        phoneNumber,
        password,
        customerType: CustomerType.LegalEntity,
        individualPerson: null,
        legalEntity: {
          cnpj,
        },
      };
    }

    setIsSubmitting(true);
    try {
      const success = await register(payload);
      if (success) {
        const loggedInUser = await login({ email, password });
        if (loggedInUser?.requiresContactInfoUpdate) {
          navigate(toPublicPath("/completar-cadastro"));
        } else if (loggedInUser) {
          navigate("/");
        } else {
          navigate(toPublicPath("/login"));
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="relative">
      {isSubmitting && <LoadingOverlay fullscreen />}
      <div className="min-h-screen bg-white flex flex-col items-center px-4 py-10">
        <Link to="/" className="mb-8">
          <img
            src={tenant?.logo?.url}
            alt="Logo"
            className="w-44 mx-auto mb-8 sm:w-48"
          />
        </Link>

        <div className="w-full flex flex-grow items-center justify-center">
        <div className="w-full max-w-3xl bg-white">
          <h2 className="text-xl md:text-2xl text-primary-600 font-semibold text-center mb-3 md:mb-6">
            {"CRIAR CONTA"}
          </h2>

          <form className="space-y-3 md:space-y-6" onSubmit={handleSubmit}>
            <div className="space-y-3">
              <button
                type="button"
                className="w-full border border-gray-300 bg-white text-gray-800 font-semibold py-3 rounded flex justify-center items-center gap-2 transition-colors hover:border-primary-600"
                onClick={handleGoogleSignUp}
              >
                <GoogleIcon size={20} />
                {"CONTINUAR COM GOOGLE"}
              </button>

              <div className="flex items-center gap-3 text-sm text-gray-500">
                <span className="h-px flex-1 bg-gray-300" />
                <span>{"ou"}</span>
                <span className="h-px flex-1 bg-gray-300" />
              </div>
            </div>

            <PersonTypeToggle value={personType} onChange={handlePersonTypeChange} className="gap-3 md:gap-6 mb-4 md:mb-6" />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 md:gap-6">
              {personType === CustomerType.IndividualPerson ? (
                <Input
                  label="CPF"
                  type="text"
                  name="cpf"
                  placeholder="000.000.000-00"
                  value={formatCpf(cpf)}
                  onChange={(e) => setCpf(unformat(e.target.value).slice(0, 11))}
                  required
                />
              ) : (
                <Input
                  label="CNPJ"
                  type="text"
                  name="cnpj"
                  placeholder="00.000.000/0000-00"
                  value={formatCnpj(cnpj)}
                  onChange={(e) => setCnpj(unformat(e.target.value).slice(0, 14))}
                  required
                />
              )}

              <Input
                label={"Telefone celular"}
                type="tel"
                name="phoneNumber"
                placeholder="(00) 00000-0000"
                value={formatPhoneNumber(phoneNumber)}
                onChange={(e) => setPhoneNumber(unformat(e.target.value))}
                required
              />

              <Input
                label={"E-mail"}
                type="email"
                name="email"
                placeholder="seu@email.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
              />

              <Input
                label={"Senha"}
                type="password"
                name="password"
                placeholder={"Crie sua senha"}
                tooltip={passwordRequirementsMessage.trim()}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
            </div>

            <p className="text-sm text-gray-600">
              {"Ao criar uma conta, você concorda com as"}{" "}
              <Link to={toPublicPath("/politicas")} className="text-primary-600 underline">
                {"Políticas do Site e Políticas de Privacidade"}
              </Link>
              .
            </p>

            <Button isLoading={isSubmitting} icon={<FaUserPlus />}
              type="submit"
              className="w-full py-3 flex justify-center items-center gap-2"
              disabled={isSubmitting}
            >
              {"CRIAR CONTA"}
            </Button>
          </form>

          <div className="w-full flex justify-center items-center text-sm gap-1 py-8 md:py-10">
            <p>{"Já possui cadastro?"}</p>
            <Link to={toPublicPath("/login")} className="text-primary-600 underline font-bold">
              {"ENTRAR"}
            </Link>
          </div>
        </div>
        </div>
      </div>
      <TrustBar />
      <Footer />
    </div>
  );
};

export default UserSignUpPage;
